import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const ProvinceMapModal = ({ isOpen, onClose, data, loading, error }) => {
  const [selectedProvince, setSelectedProvince] = useState(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Coordinates for all provinces/states
  const provinceCoordinates = {
    // Canadian Provinces
    "Ontario": [51.2538, -85.3232],
    "Quebec": [52.9399, -73.5491],
    "British Columbia": [53.7267, -127.6476],
    "Alberta": [55.0000, -115.0000],
    "Manitoba": [55.1833, -97.8333],
    "Saskatchewan": [55.0000, -106.0000],
    "Nova Scotia": [45.0000, -63.0000],
    "New Brunswick": [46.4984, -66.1597],
    "Newfoundland and Labrador": [53.1355, -57.6604],
    "Prince Edward Island": [46.5107, -63.4168],
    
    // US States
    "Alabama": [32.7794, -86.8287],
    "Alaska": [64.0685, -152.2782],
    "Arizona": [34.2744, -111.6602],
    "Arkansas": [34.8938, -92.4426],
    "California": [36.7783, -119.4179],
    "Colorado": [39.5501, -105.7821],
    "Connecticut": [41.5928, -72.7074],
    "Delaware": [38.9896, -75.5050],
    "District of Columbia": [38.9072, -77.0369],
    "Florida": [27.9944, -81.7603],
    "Georgia": [32.6415, -83.4426],
    "Hawaii": [20.2927, -156.3737],
    "Idaho": [44.0682, -114.7420],
    "Illinois": [40.0417, -89.1965],
    "Indiana": [39.8942, -86.2816],
    "Iowa": [42.0751, -93.4960],
    "Kansas": [38.4937, -98.3200],
    "Kentucky": [37.5347, -85.3021],
    "Louisiana": [31.0689, -91.9968],
    "Maine": [45.3695, -69.2428],
    "Maryland": [39.0550, -76.7909],
    "Massachusetts": [42.2596, -71.8083],
    "Michigan": [44.3467, -85.4102],
    "Minnesota": [46.2807, -94.3053],
    "Mississippi": [32.7364, -89.6678],
    "Missouri": [38.4623, -92.3020],
    "Montana": [47.0527, -109.6333],
    "Nebraska": [41.5378, -99.7951],
    "Nevada": [39.3289, -116.6312],
    "New Hampshire": [43.6805, -71.5811],
    "New Jersey": [40.1907, -74.6728],
    "New Mexico": [34.4071, -106.1126],
    "New York": [42.9538, -75.5268],
    "North Carolina": [35.5557, -79.3877],
    "North Dakota": [47.4501, -100.4659],
    "Ohio": [40.2862, -82.7937],
    "Oklahoma": [35.5889, -97.4943],
    "Oregon": [43.9336, -120.5583],
    "Pennsylvania": [40.8781, -77.7996],
    "Rhode Island": [41.6762, -71.5562],
    "South Carolina": [33.9169, -80.8964],
    "South Dakota": [44.4443, -100.2263],
    "Tennessee": [35.8600, -86.3505],
    "Texas": [31.4757, -99.3312],
    "Utah": [39.3055, -111.6703],
    "Vermont": [44.0687, -72.6658],
    "Virginia": [37.5215, -78.8537],
    "Washington": [47.3826, -120.4472],
    "West Virginia": [38.6409, -80.6227],
    "Wisconsin": [44.6243, -89.9941],
    "Wyoming": [43.0799, -107.6893],
    
    // Other regions in data
    "Aragon": [41.5976, -0.9057],
    "Brittany": [48.2020, -2.9326],
    "Harju County": [59.3396, 25.1332]
  };

  // Function to get color based on AUM value
  const getCircleColor = (aum) => {
    if (!aum) return '#FFD700'; // Default yellow color for zero values
    
    // Get the maximum AUM value
    const maxAum = Math.max(...Object.values(data || {}).map(d => d.assets_under_management || 0));
    
    // Calculate the ratio of current AUM to maximum AUM (0 to 1)
    const ratio = aum / maxAum;
    
    // Based on the ratio, create a color ranging from yellow to red
    // Yellow: #FFD700, Orange: #FFA500, Red: #FF0000
    if (ratio < 0.33) {
      // Yellow to orange gradient
      return `rgb(255, ${Math.floor(215 - ratio * 3 * 100)}, 0)`;
    } else if (ratio < 0.66) {
      // Orange to red gradient
      return `rgb(255, ${Math.floor(165 - (ratio - 0.33) * 3 * 165)}, 0)`;
    } else {
      // Deep red for highest values
      return '#FF0000';
    }
  };
  
  // Calculate bubble size based on AUM
  const getBubbleSize = (aum) => {
    if (!aum) return 10;
    const minSize = 10;
    const maxSize = 40;
    const maxAum = Math.max(...Object.values(data || {}).map(d => d.assets_under_management || 0));
    const size = (aum / maxAum) * (maxSize - minSize) + minSize;
    return Math.max(minSize, Math.min(size, maxSize));
  };

  // Initialize map after component mounts
  useEffect(() => {
    if (!isOpen || loading || error || !data || !mapContainerRef.current) return;

    // Create map if it doesn't exist
    if (!mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current).setView([39.8283, -98.5795], 4);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(mapRef.current);
    }

    // Clear existing markers
    if (mapRef.current) {
      mapRef.current.eachLayer(layer => {
        if (layer instanceof L.CircleMarker) {
          mapRef.current.removeLayer(layer);
        }
      });
    }

    // Add markers for each province/state
    Object.entries(data || {}).forEach(([province, values]) => {
      const coordinates = provinceCoordinates[province];
      if (!coordinates) return;
      
      const bubbleSize = getBubbleSize(values.assets_under_management);
      const circleColor = getCircleColor(values.assets_under_management);
      
      const marker = L.circleMarker(coordinates, {
        radius: bubbleSize,
        fillColor: circleColor,
        color: '#000',
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8
      }).addTo(mapRef.current);
      
      marker.bindPopup(`
        <div style="padding: 8px;">
          <h4 style="font-weight: bold; font-size: 14px; margin-bottom: 8px;">${province}</h4>
          <p style="font-size: 12px; margin-bottom: 4px;">
            AUM: ${formatCurrency(values.assets_under_management)}
          </p>
          <p style="font-size: 12px; margin-bottom: 0;">
            Gross Production: ${formatCurrency(values.gross_production)}
          </p>
        </div>
      `);
    });

    // Clean up on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isOpen, data, loading, error]);

  // Debug - log missing coordinates
  useEffect(() => {
    if (data) {
      const missingCoordinates = Object.keys(data).filter(province => !provinceCoordinates[province]);
      if (missingCoordinates.length > 0) {
        console.warn('Missing coordinates for:', missingCoordinates);
      }
    }
  }, [data]);

  if (!isOpen) return null;
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-succession-bg-primary rounded-lg shadow-xl w-full max-w-4xl border border-succession-border">
          <div className="px-4 py-5 border-b border-succession-border flex justify-between items-center">
            <h3 className="text-lg font-medium text-succession-text-primary">Seller Heat Map</h3>
            <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-4 py-5">
            <p className="text-succession-text-secondary">Loading seller data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-succession-bg-primary rounded-lg shadow-xl w-full max-w-4xl border border-succession-border">
          <div className="px-4 py-5 border-b border-succession-border flex justify-between items-center">
            <h3 className="text-lg font-medium text-succession-text-primary">Seller Heat Map</h3>
            <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-4 py-5">
            <p className="text-succession-error">Error loading map data.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-succession-bg-primary rounded-lg shadow-xl w-full max-w-4xl border border-succession-border">
        <div className="px-4 py-5 border-b border-succession-border flex justify-between items-center">
          <h3 className="text-lg font-medium text-succession-text-primary">Seller Heat Map</h3>
          <button onClick={onClose} className="text-succession-text-secondary hover:text-succession-text-primary">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="px-4 py-5">
          <div className="mb-2 flex justify-between items-center text-succession-text-secondary">
            <div className="flex items-center">
              <span className="inline-block w-4 h-4 rounded-full mr-1" style={{backgroundColor: '#FFD700'}}></span>
              <span className="text-xs">Low AUM</span>
            </div>
            <div className="flex items-center">
              <span className="inline-block w-4 h-4 rounded-full mr-1" style={{backgroundColor: '#FFA500'}}></span>
              <span className="text-xs">Medium AUM</span>
            </div>
            <div className="flex items-center">
              <span className="inline-block w-4 h-4 rounded-full mr-1" style={{backgroundColor: '#FF0000'}}></span>
              <span className="text-xs">High AUM</span>
            </div>
          </div>
          <div ref={mapContainerRef} style={{ height: '500px', width: '100%' }} />
        </div>
      </div>
    </div>
  );
};

export default ProvinceMapModal; 